import {
  BellIcon,
  CalendarClockIcon,
  CalendarDaysIcon,
  ClapperboardIcon,
  ClipboardListIcon,
  ClockIcon,
  ContactIcon,
  FileIcon,
  FileTextIcon,
  FolderKanbanIcon,
  FolderOpenIcon,
  HandshakeIcon,
  KeyRoundIcon,
  LayoutDashboardIcon,
  ReceiptIcon,
  ScaleIcon,
  ScrollTextIcon,
  StickyNoteIcon,
  type LucideIcon,
} from 'lucide-react'

/**
 * Les pages d'un client (#285) : les anciens onglets de la fiche, devenus des
 * adresses (`/clients/{id}/{slug}`) et la première partie du menu latéral.
 * L'aperçu est la fiche elle-même (`/clients/{id}`), sans segment.
 */
export type ClientPageSlug =
  | 'apercu'
  | 'contacts'
  | 'echanges'
  | 'relances'
  | 'rendez-vous'
  | 'notes'
  | 'affaires'
  | 'projets'
  | 'taches'
  | 'documents'
  | 'secrets'
  | 'formations'
  | 'temps'
  | 'facturation'
  | 'soumissions'
  | 'terms'
  | 'questionnaires'
  | 'journal'

export interface ClientPage {
  slug: ClientPageSlug
  label: string
  icon: LucideIcon
  /** Lot 38 : `nav-client-{key}-link`, en anglais et stable. */
  key: string
}

export const CLIENT_PAGES: readonly ClientPage[] = [
  { slug: 'apercu', label: 'Aperçu', icon: LayoutDashboardIcon, key: 'overview' },
  { slug: 'contacts', label: 'Contacts', icon: ContactIcon, key: 'contacts' },
  { slug: 'echanges', label: 'Échanges', icon: CalendarDaysIcon, key: 'interactions' },
  { slug: 'relances', label: 'Relances', icon: BellIcon, key: 'reminders' },
  { slug: 'rendez-vous', label: 'Rendez-vous', icon: CalendarClockIcon, key: 'meetings' },
  { slug: 'notes', label: 'Notes', icon: StickyNoteIcon, key: 'notes' },
  { slug: 'affaires', label: 'Affaires', icon: HandshakeIcon, key: 'opportunities' },
  { slug: 'projets', label: 'Projets', icon: FolderOpenIcon, key: 'projects' },
  { slug: 'taches', label: 'Tâches', icon: FolderKanbanIcon, key: 'tasks' },
  { slug: 'documents', label: 'Documents', icon: FileIcon, key: 'documents' },
  { slug: 'secrets', label: 'Secrets', icon: KeyRoundIcon, key: 'secrets' },
  { slug: 'formations', label: 'Formations', icon: ClapperboardIcon, key: 'trainings' },
  { slug: 'temps', label: 'Temps du client', icon: ClockIcon, key: 'time' },
  { slug: 'facturation', label: 'Facturation', icon: ReceiptIcon, key: 'billing' },
  { slug: 'soumissions', label: 'Soumissions', icon: FileTextIcon, key: 'quotes' },
  // 1.85.0 : les conditions générales du client — les globales, ou sa version propre.
  // Le chemin est celui du contrat (`web_path` des refus), en anglais comme `secrets`.
  { slug: 'terms', label: 'Conditions', icon: ScaleIcon, key: 'terms' },
  {
    slug: 'questionnaires',
    label: 'Questionnaires',
    icon: ClipboardListIcon,
    key: 'questionnaires',
  },
  { slug: 'journal', label: 'Journal', icon: ScrollTextIcon, key: 'activity' },
]

const BY_SLUG = new Map(CLIENT_PAGES.map((page) => [page.slug, page]))

export function clientPage(slug: string | undefined): ClientPage | undefined {
  return slug ? BY_SLUG.get(slug as ClientPageSlug) : undefined
}

/**
 * L'ancien onglet (`?tab=`) → la page. `audit` s'appelle désormais Journal ;
 * un onglet inconnu mène à l'aperçu.
 */
export function pageForLegacyTab(tab: unknown): ClientPageSlug {
  if (tab === 'audit') return 'journal'
  if (typeof tab === 'string' && BY_SLUG.has(tab as ClientPageSlug) && tab !== 'journal') {
    return tab as ClientPageSlug
  }
  return 'apercu'
}

/** Le chemin d'une page d'un client, identifiants compris. */
export function clientPagePath(clientId: string, slug: ClientPageSlug): string {
  return slug === 'apercu' ? `/clients/${clientId}` : `/clients/${clientId}/${slug}`
}

/**
 * La page d'un client que désigne un chemin, ou `null` : sert au menu (entrée
 * active) et au sélecteur (garder la même page en changeant de client). Les
 * fiches sous le client (`/clients/{id}/echanges/{id}`, un document) ne sont
 * pas des pages du menu : elles comptent pour la page de leur liste.
 */
export function clientPageOf(pathname: string): { clientId: string; slug: ClientPageSlug } | null {
  const match = /^\/clients\/([0-9a-f]{24})(?:\/([a-z-]+))?/.exec(pathname)
  if (!match?.[1]) return null
  const slug = match[2] ? clientPage(match[2])?.slug : 'apercu'
  return slug ? { clientId: match[1], slug } : null
}
